import { useState, useEffect, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ProjectDetailTabs } from "shared/utils/enum";
import { routeConstant } from "shared/routes/routeConstant";
import { canUploadRevision, startWorkingState } from "./canUploadRevision";
import {
  ProjectDetailService,
  ProjectNextStatusService,
} from "shared/services/project";
import {
  GetProjectRevisionService,
  GetProjectRevisionListService,
  MarkRevisionViewedService,
  UpdateRevisionDesignerUploadsService,
  DeleteDesignerUploadService,
  UploadDesignerFile,
  SetRevisionMediaSortService,
  SetRevisionInProgressService,
} from "shared/services/revision";
import {
  GetProjectChatUnreadService,
  MarkProjectChatReadService,
} from "shared/services/notification";
import { handleErrors, getDisplayFileName } from "shared/utils/helper";
import { getUploadKind } from "shared/constants/officeFiles";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets/icons";
import classNames from "classnames";
import styles from "./styles.module.scss";
import CustomTabs from "shared/components/common/customTabs";
import CustomProjectCard from "shared/components/common/customProjectCard";
import FilterDropdown from "shared/components/common/filterDropdown";
import ChatBoard from "shared/components/dashboardCommon/tabsContent/chatBoard";
import RevisionsMedia from "shared/components/dashboardCommon/tabsContent/revisionsMedia";
import StartWorkingConfirmationModal from "shared/modal/startWorkingConfirmation";
import EditRevisionModal, {
  type EditFileItem,
} from "shared/components/designer/editRevision";
import {
  applyRevisionRename,
  renamedSelectionLabel,
} from "./applyRevisionRename";
import FeedbackRequest from "shared/components/dashboardCommon/tabsContent/feedbackRequest";
import ProjectOverview from "shared/components/dashboardCommon/tabsContent/projectOverview";
import ProjectCardSkeleton from "shared/components/common/projectCardSkeleton";
import Spinner from "shared/components/common/spinner";
import RevisionMediaSkeleton from "shared/components/common/revisionMediaSkeleton";
import ProjectCollaborators from "shared/components/dashboardCommon/tabsContent/projectCollaborators";

const DesignerProjectDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [revisionsPage, setRevisionsPage] = useState(1);
  const [hasMoreRevisionsMedia, setHasMoreRevisionsMedia] = useState(true);
  const [revisionList, setRevisionList] = useState<any[]>([]);
  const [revisionListLoading, setRevisionListLoading] = useState(false);
  const [selectedRevision, setSelectedRevision] =
    useState<string>("Select a revision");
  const [selectedRevisionId, setSelectedRevisionId] = useState<
    number | undefined
  >(undefined);
  const [revisionPage, setRevisionPage] = useState(1);
  const [hasMoreRevisions, setHasMoreRevisions] = useState(true);
  const [, setTotalRevisionsCount] = useState(0);
  const [selectedChatBoard, setSelectedChatBoard] =
    useState<string>("Client Chat Board");
  const [uploadSortBy, setUploadSortBy] = useState<
    "default" | "name_asc" | "name_desc" | "modified_asc" | "modified_desc"
  >("default");
  const [chatUnread, setChatUnread] = useState(0);
  const [editingRevision, setEditingRevision] = useState<{
    id: number;
    title: string;
    designerComment: string;
    internalComment: string;
    files: EditFileItem[];
    isFinalPlans: boolean;
  } | null>(null);

  const allTabs = [
    {
      icon: <Icons.TabImage />,
      label: "Revisions & Media",
      value: ProjectDetailTabs.REVISIONS_MEDIA,
    },
    {
      icon: <Icons.Chat />,
      label: "Chat Board",
      value: ProjectDetailTabs.CHAT_BOARD,
      showDot: chatUnread > 0,
    },
    {
      icon: <Icons.TabFeedback />,
      label: "Revision Requests",
      value: ProjectDetailTabs.FEEDBACK_REQUEST,
    },
    {
      icon: <Icons.TabOverview />,
      label: "Project Overview",
      value: ProjectDetailTabs.PROJECT_OVERVIEW,
    },
    {
      icon: <Icons.TabOverview />,
      label: "Contact Info",
      value: ProjectDetailTabs.COLLABORATORS,
    },
  ];

  const finalProjectData = projectData || location.state?.projectData || {};
  const isFinalReviewer =
    (finalProjectData?.role || "").toLowerCase() === "final_reviewer";
  const tabs = isFinalReviewer
    ? allTabs.filter((t) => t.value !== ProjectDetailTabs.FEEDBACK_REQUEST)
    : allTabs;
  const tabOrder = tabs.map((t) => t.value);

  // Initialize activeTab from URL params or default to first tab
  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabOrder.includes(tabParam as ProjectDetailTabs)) {
      return tabParam as ProjectDetailTabs;
    }
    return tabOrder[0];
  };

  const [activeTab, setActiveTab] =
    useState<ProjectDetailTabs>(getInitialTab());

  // Update URL when tab changes
  const handleTabChange = (tabValue: string) => {
    const newTab = tabValue as ProjectDetailTabs;
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  useEffect(() => {
    fetchProjectDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chat-board unread bubble: fetch the per-project unread chat count on load.
  useEffect(() => {
    if (!id) return;
    GetProjectChatUnreadService(parseInt(id))
      .then((r) => {
        const data = r.data?.data ?? r.data;
        setChatUnread(Number(data?.unread_count ?? 0));
      })
      .catch(() => {});
  }, [id]);

  // Clear the chat unread bubble when the user opens the Chat Board tab.
  useEffect(() => {
    if (id && activeTab === ProjectDetailTabs.CHAT_BOARD && chatUnread > 0) {
      MarkProjectChatReadService(parseInt(id)).catch(() => {});
      setChatUnread(0);
    }
  }, [activeTab, id, chatUnread]);

  // Live chat unread bubble: bump the count when a comment/reply notification
  // for this project arrives over the socket (no page refresh needed).
  useEffect(() => {
    if (!id) return;
    const handleNewNotification = (event: Event) => {
      const data = (event as CustomEvent<any>)?.detail;
      const type = String(data?.type || "").toLowerCase();
      const isChat = type === "new_comment" || type === "new_reply";
      if (!isChat || Number(data?.project_id) !== parseInt(id)) return;
      if (activeTab === ProjectDetailTabs.CHAT_BOARD) {
        MarkProjectChatReadService(parseInt(id)).catch(() => {});
        return;
      }
      setChatUnread((c) => c + 1);
    };
    window.addEventListener("notification:new", handleNewNotification);
    return () => {
      window.removeEventListener("notification:new", handleNewNotification);
    };
  }, [id, activeTab]);

  useEffect(() => {
    const fromChatBoard = location.state?.fromChatBoard;
    if (fromChatBoard) {
      const chatBoardTab = ProjectDetailTabs.CHAT_BOARD;
      setActiveTab(chatBoardTab);
      setSearchParams({ tab: chatBoardTab });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync activeTab with URL params on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabOrder.includes(tabParam as ProjectDetailTabs)) {
      setActiveTab(tabParam as ProjectDetailTabs);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // When designer is final_reviewer, Feedback Request tab is hidden; switch away if it was selected
  useEffect(() => {
    if (
      isFinalReviewer &&
      activeTab === ProjectDetailTabs.FEEDBACK_REQUEST &&
      tabOrder.length > 0
    ) {
      const firstTab = tabOrder[0] as ProjectDetailTabs;
      setActiveTab(firstTab);
      setSearchParams({ tab: firstTab });
    }
  }, [isFinalReviewer, activeTab, tabOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id && projectData && activeTab === ProjectDetailTabs.REVISIONS_MEDIA) {
      setRevisionsPage(1);
      setRevisions([]);
      fetchRevisions(1, false);
    }
  }, [id, projectData, activeTab, uploadSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id && activeTab === ProjectDetailTabs.FEEDBACK_REQUEST) {
      setSelectedRevision("Select a revision");
      setSelectedRevisionId(undefined);
      fetchRevisionList();
    }
  }, [id, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjectDetail = async () => {
    if (!id) {
      setProjectData(location.state?.projectData || {});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await ProjectDetailService(parseInt(id));

      if (response.status === 200) {
        const data = response.data?.data || response.data || {};
        setProjectData(data);
      }
    } catch (error: any) {
      handleErrors(error);
      setProjectData(location.state?.projectData || {});
    } finally {
      setLoading(false);
    }
  };

  const fetchRevisions = async (page: number = 1, append: boolean = false) => {
    if (!id || revisionsLoading) return;

    try {
      setRevisionsLoading(true);
      const limit = 10;
      const response = await GetProjectRevisionService(
        parseInt(id),
        page,
        limit,
        uploadSortBy === "default" ? undefined : uploadSortBy,
      );

      if (response.status === 200) {
        let apiRevisions =
          response.data?.data ||
          response.data?.revisions ||
          response.data ||
          [];

        if (!Array.isArray(apiRevisions)) {
          if (apiRevisions && typeof apiRevisions === "object") {
            apiRevisions =
              Object.values(apiRevisions).filter((item) =>
                Array.isArray(item),
              )[0] || [];
          } else {
            apiRevisions = [];
          }
        }

        const total = response.data?.total;

        if (append) {
          setRevisions((prev) => [...prev, ...apiRevisions]);
        } else {
          setRevisions(apiRevisions);
        }

        setRevisionsPage(page);

        const currentTotal = append
          ? revisions.length + apiRevisions.length
          : apiRevisions.length;
        const hasMore =
          apiRevisions.length === limit &&
          (total === undefined || currentTotal < total);
        setHasMoreRevisionsMedia(hasMore);
      }
    } catch (error: any) {
      handleErrors(error);
      if (!append) {
        setRevisions([]);
        setHasMoreRevisionsMedia(false);
      }
    } finally {
      setRevisionsLoading(false);
    }
  };

  const loadMoreRevisionsMedia = () => {
    if (hasMoreRevisionsMedia && !revisionsLoading) {
      fetchRevisions(revisionsPage + 1, true);
    }
  };

  // Designers can edit files only on the latest revision while it awaits the client.
  const latestMediaRevision = revisions[0];

  // Record that this designer has viewed the current revision (dismisses their
  // "back in your queue" banner). Fire-and-forget, once per revision id.
  // Skipped when arriving via the banner's "View Project" button, which must
  // leave the notification card in place.
  const keepReviewBanner = !!location.state?.keepReviewBanner;
  const currentRevisionId =
    revisions[0]?.revision_id ??
    projectData?.latest_revision?.revision_id ??
    location.state?.projectData?.latest_revision?.revision_id;
  const markedViewedRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      !keepReviewBanner &&
      currentRevisionId &&
      markedViewedRef.current !== Number(currentRevisionId)
    ) {
      markedViewedRef.current = Number(currentRevisionId);
      MarkRevisionViewedService(Number(currentRevisionId)).catch(() => {
        // fire-and-forget: a failed view-record must not block the page
      });
    }
  }, [currentRevisionId, keepReviewBanner]);

  // Exception: the final-plans revision stays editable indefinitely, even
  // after project completion, so the team can correct final plans if needed.
  const finalPlansRevision = revisions.find((r: any) => r.is_final_plans);

  const editableRevisionId = finalPlansRevision
    ? finalPlansRevision.revision_id?.toString() || undefined
    : latestMediaRevision &&
        (latestMediaRevision.status || "")
          .toLowerCase()
          .replace(/[-\s]/g, "_") === "waiting_for_client_response"
      ? latestMediaRevision.revision_id?.toString() || undefined
      : undefined;

  const handleEditRevision = (revisionIdStr: string) => {
    const rev = revisions.find(
      (r) => (r.revision_id?.toString() || "") === revisionIdStr,
    );
    if (!rev) return;
    const uploads = rev.designer_uploads || [];
    const getFileNameFromPath = (path: string): string =>
      getDisplayFileName(path);
    const files: EditFileItem[] = uploads.map((u: any) => {
      const name = u.name || u.file_name || getFileNameFromPath(u.path || "");
      return {
        id: u.revision_designer_upload_id,
        src: u.path,
        type: getUploadKind({ type: u.type, name }),
        name,
      };
    });
    setEditingRevision({
      id: Number(rev.revision_id),
      title: rev.title || "",
      designerComment: rev.designer_comment || "",
      internalComment: rev.internal_comment || "",
      files,
      isFinalPlans: !!rev.is_final_plans,
    });
  };

  const handleFilesSaved = () => {
    setRevisionsPage(1);
    setRevisions([]);
    fetchRevisions(1, false);
  };

  // The Revision Requests picker has its own endpoint; mirror renames into it.
  const handleRevisionSaved = (revisionId: number, title: string) => {
    handleFilesSaved();
    setRevisionList((prev) => applyRevisionRename(prev, revisionId, title));
    setSelectedRevision((prev) =>
      renamedSelectionLabel(prev, selectedRevisionId, revisionId, title),
    );
  };

  // Inline edits on the Revisions & Media strip (editable revision only). Each
  // persists immediately and refreshes the list. They throw on failure so the
  // strip can revert its optimistic state.
  const handleReorderRevisionFiles = async (
    revisionIdStr: string,
    orderedFileIds: string[],
  ) => {
    try {
      const payload = orderedFileIds.map((id, i) => ({
        id: Number(id),
        order: i + 1,
      }));
      const res = await UpdateRevisionDesignerUploadsService(
        Number(revisionIdStr),
        payload,
      );
      if (res.status === 200 || res.status === 201) {
        toastMessage("success", "File order updated.");
        handleFilesSaved();
      }
    } catch (error) {
      handleErrors(error);
      throw error;
    }
  };

  const handleDeleteRevisionFile = async (
    _revisionIdStr: string,
    fileId: string,
  ) => {
    try {
      const res = await DeleteDesignerUploadService(Number(fileId));
      if (res.status === 200 || res.status === 201) {
        toastMessage("success", "File deleted.");
        handleFilesSaved();
      }
    } catch (error) {
      handleErrors(error);
      throw error;
    }
  };

  const handleChangeRevisionSort = async (
    revisionIdStr: string,
    mediaSort:
      | "name_asc"
      | "name_desc"
      | "modified_asc"
      | "modified_desc"
      | null,
  ) => {
    try {
      const res = await SetRevisionMediaSortService(
        Number(revisionIdStr),
        mediaSort,
      );
      if (res.status === 200 || res.status === 201) {
        toastMessage("success", "Sort order updated.");
        handleFilesSaved();
      }
    } catch (error) {
      handleErrors(error);
      throw error;
    }
  };

  const handleAddRevisionFiles = async (
    revisionIdStr: string,
    files: File[],
  ) => {
    try {
      const rev = revisions.find(
        (r) => (r.revision_id?.toString() || "") === revisionIdStr,
      );
      const existingIds = (rev?.designer_uploads || []).map((u: any) =>
        Number(u.revision_designer_upload_id),
      );
      const newIds: number[] = [];
      for (const file of files) {
        const id = await UploadDesignerFile(file);
        newIds.push(id);
      }
      const payload = [...existingIds, ...newIds].map((id, i) => ({
        id,
        order: i + 1,
      }));
      const res = await UpdateRevisionDesignerUploadsService(
        Number(revisionIdStr),
        payload,
      );
      if (res.status === 200 || res.status === 201) {
        toastMessage("success", "Files added.");
        handleFilesSaved();
      }
    } catch (error) {
      handleErrors(error);
      throw error;
    }
  };

  // The project card hides its own upload button on the detail page, so the tab carries it.
  const latestRevisionStatus = revisions[0]?.status;
  const showUploadRevision = canUploadRevision({
    role: finalProjectData?.role,
    projectStatus: finalProjectData?.status,
    isArchived: !!finalProjectData?.is_archived,
    latestRevisionStatus,
  });

  const startWorking = startWorkingState({
    role: finalProjectData?.role,
    isArchived: !!finalProjectData?.is_archived,
    latestRevisionStatus,
  });
  const showStartWorking = startWorking === "enabled";

  const [confirmStartWorking, setConfirmStartWorking] = useState(false);
  const [startingWork, setStartingWork] = useState(false);

  const handleStartWorking = async () => {
    if (!currentRevisionId) return;

    setConfirmStartWorking(false);
    try {
      setStartingWork(true);
      const response = await SetRevisionInProgressService(
        Number(currentRevisionId),
      );

      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Revision marked as in progress!");
        fetchRevisions(1, false);
      } else {
        handleErrors(response);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setStartingWork(false);
    }
  };

  const handleUploadRevision = () => {
    const uploadProjectId =
      finalProjectData?.project_id ?? (id ? parseInt(id) : undefined);
    navigate(
      `${routeConstant.designer.home.uploadRevision.path}?projectId=${uploadProjectId}`,
      {
        state: {
          projectId: uploadProjectId,
          projectTitle: finalProjectData?.name || finalProjectData?.title,
        },
      },
    );
  };

  const fetchRevisionList = async (
    page: number = 1,
    append: boolean = false,
  ) => {
    if (!id || revisionListLoading) return;

    try {
      setRevisionListLoading(true);
      const limit = 10;
      const response = await GetProjectRevisionListService(
        parseInt(id),
        page,
        limit,
      );

      if (response.status === 200) {
        const data =
          response.data?.data ||
          response.data?.revisions ||
          response.data ||
          [];
        const revisions = Array.isArray(data) ? data : [];
        const total = response.data?.total;

        if (append) {
          setRevisionList((prev) => [...prev, ...revisions]);
        } else {
          setRevisionList(revisions);

          if (revisions.length > 0) {
            const firstRevision = revisions[0];
            setSelectedRevision(
              firstRevision.name ||
              `Revision ${firstRevision.revision_id || ""}`,
            );
            setSelectedRevisionId(firstRevision.revision_id);
          }
        }

        setRevisionPage(page);
        if (total !== undefined) {
          setTotalRevisionsCount(total);
        }

        if (append) {
          const newTotal = revisionList.length + revisions.length;
          const hasMore =
            revisions.length === limit &&
            (total === undefined || newTotal < total);
          setHasMoreRevisions(hasMore);
        } else {
          const hasMore =
            revisions.length === limit &&
            (total === undefined || revisions.length < total);
          setHasMoreRevisions(hasMore);
        }
      }
    } catch (error: any) {
      handleErrors(error);
      if (!append) {
        setRevisionList([]);
        setHasMoreRevisions(false);
      }
    } finally {
      setRevisionListLoading(false);
    }
  };

  const loadMoreRevisions = () => {
    if (hasMoreRevisions && !revisionListLoading) {
      fetchRevisionList(revisionPage + 1, true);
    }
  };

  const latestRevisionWithStatus =
    revisions.length > 0 ? revisions[0] : finalProjectData?.latest_revision;

  const chatBoards = finalProjectData?.chat_boards || [];

  const generalBoard = chatBoards.find(
    (board: any) => board.type === "general",
  );
  const internalBoard = chatBoards.find(
    (board: any) => board.type === "internal",
  );

  const chatBoardOptions = [];
  if (generalBoard) chatBoardOptions.push("Client Chat Board");
  if (internalBoard) chatBoardOptions.push("Team Chat Board");

  const getSelectedChatBoardId = (): string | number | undefined => {
    if (selectedChatBoard === "Client Chat Board" && generalBoard) {
      return generalBoard.chat_board_id;
    }
    if (selectedChatBoard === "Team Chat Board" && internalBoard) {
      return internalBoard.chat_board_id;
    }
    return generalBoard?.chat_board_id || internalBoard?.chat_board_id;
  };

  useEffect(() => {
    if (chatBoards.length > 0) {
      if (!generalBoard && internalBoard) {
        setSelectedChatBoard("Team Chat Board");
      } else if (generalBoard) {
        setSelectedChatBoard("Client Chat Board");
      }
    }
  }, [finalProjectData?.chat_boards]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentIndex = tabOrder.indexOf(activeTab);

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevTab = tabOrder[currentIndex - 1] as ProjectDetailTabs;
      handleTabChange(prevTab);
    }
  };

  const handleNext = () => {
    if (currentIndex < tabOrder.length - 1) {
      const nextTab = tabOrder[currentIndex + 1] as ProjectDetailTabs;
      handleTabChange(nextTab);
    }
  };

  const handleProceedToNextPhase = async () => {
    const isClientOrCollaborator =
      location.pathname.includes("/client") ||
      location.pathname.includes("/collaborator");
    if (!isClientOrCollaborator) {
      toastMessage(
        "error",
        "This action is only available to clients and collaborators",
      );
      return;
    }

    if (!id) {
      toastMessage("error", "Project ID is missing");
      return;
    }

    try {
      const response = await ProjectNextStatusService(parseInt(id));

      if (response.status === 200) {
        toastMessage("success", "Project moved to next phase successfully");
        await fetchProjectDetail();
      }
    } catch (error: any) {
      handleErrors(error);
    }
  };

  if (loading) {
    return (
      <div className={classNames(styles.pageWrapper, "flex flex-col gap-5")}>
        <ProjectCardSkeleton count={1} />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.pageWrapper, "flex flex-col gap-5 h-full")}
    >
      <CustomProjectCard
        id={finalProjectData?.project_id || (id ? parseInt(id) : undefined)}
        image={finalProjectData?.project_asset?.path || finalProjectData?.image}
        title={finalProjectData?.name || finalProjectData?.title}
        currentRevision={latestRevisionWithStatus}
        status={finalProjectData?.status}
        isArchived={!!finalProjectData?.is_archived}
        onProceedToNextPhase={handleProceedToNextPhase}
        onChatIconClick={() => handleTabChange(ProjectDetailTabs.CHAT_BOARD)}
        role={finalProjectData?.role}
      />

      <div className={styles.box}>
        <div className={styles.tabsNavigationRow}>
          <button
            className={styles.arrowButton}
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <Icons.ChevLeft />
          </button>

          <CustomTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            rightSideContent={
              <>
                <div className="hidden md:block">
                  {activeTab === ProjectDetailTabs.CHAT_BOARD &&
                    chatBoardOptions.length > 0 && (
                      <FilterDropdown
                        label=""
                        options={chatBoardOptions}
                        selected={selectedChatBoard}
                        onSelect={(value: string) =>
                          setSelectedChatBoard(value)
                        }
                      />
                    )}
                  {activeTab === ProjectDetailTabs.FEEDBACK_REQUEST && (
                    <FilterDropdown
                      label=""
                      options={revisionList.map(
                        (rev) =>
                          rev.name || `Revision ${rev.revision_id || ""}`,
                      )}
                      selected={selectedRevision || "Select a revision"}
                      menuWidth="fit-content"
                      maxHeight="200px"
                      menuPosition="right"
                      loading={revisionListLoading}
                      onScrollEnd={
                        hasMoreRevisions && !revisionListLoading
                          ? loadMoreRevisions
                          : undefined
                      }
                      onSelect={(value) => {
                        setSelectedRevision(value);
                        const selected = revisionList.find(
                          (rev) =>
                            (rev.name ||
                              `Revision ${rev.revision_id || ""}`) === value,
                        );
                        setSelectedRevisionId(selected?.revision_id);
                      }}
                    />
                  )}
                  {/*
                    Tab-level sort dropdown — superseded by the per-revision,
                    persisted sort (designer sets it on each revision card, and
                    it is shared with all viewers). Kept here, commented out, in
                    case we want a global view-level sort again.

                  {activeTab === ProjectDetailTabs.REVISIONS_MEDIA && (
                    <FilterDropdown
                      label=""
                      options={[
                        "Default Order",
                        "Date Modified ↓",
                        "Date Modified ↑",
                        "Name A→Z",
                        "Name Z→A",
                      ]}
                      selected={
                        uploadSortBy === "default"
                          ? "Default Order"
                          : uploadSortBy === "modified_desc"
                            ? "Date Modified ↓"
                            : uploadSortBy === "modified_asc"
                              ? "Date Modified ↑"
                              : uploadSortBy === "name_asc"
                                ? "Name A→Z"
                                : "Name Z→A"
                      }
                      menuWidth="fit-content"
                      maxHeight="200px"
                      menuPosition="right"
                      onSelect={(value) => {
                        const map: Record<
                          string,
                          | "default"
                          | "name_asc"
                          | "name_desc"
                          | "modified_asc"
                          | "modified_desc"
                        > = {
                          "Default Order": "default",
                          "Date Modified ↓": "modified_desc",
                          "Date Modified ↑": "modified_asc",
                          "Name A→Z": "name_asc",
                          "Name Z→A": "name_desc",
                        };
                        setUploadSortBy(map[value] || "default");
                      }}
                    />
                  )}
                  */}
                </div>
              </>
            }
          />

          <button
            className={styles.arrowButton}
            onClick={handleNext}
            disabled={currentIndex === tabOrder.length - 1}
          >
            <Icons.ChevRight />
          </button>
        </div>

        <div className={classNames(styles.tabsContentContainer, "pt-3")}>
          {activeTab === ProjectDetailTabs.REVISIONS_MEDIA && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {revisionsLoading && revisions.length === 0 ? (
                <RevisionMediaSkeleton count={2} />
              ) : (
                <>
                  <RevisionsMedia
                    revisions={revisions}
                    showBadge={false}
                    hasMore={hasMoreRevisionsMedia}
                    isLoadingMore={revisionsLoading}
                    onLoadMore={loadMoreRevisionsMedia}
                    editableRevisionId={editableRevisionId}
                    onEditRevision={
                      finalProjectData?.is_archived ? undefined : handleEditRevision
                    }
                    onReorderFiles={
                      finalProjectData?.is_archived
                        ? undefined
                        : handleReorderRevisionFiles
                    }
                    onDeleteFile={
                      finalProjectData?.is_archived
                        ? undefined
                        : handleDeleteRevisionFile
                    }
                    onAddFiles={
                      finalProjectData?.is_archived
                        ? undefined
                        : handleAddRevisionFiles
                    }
                    onChangeSort={
                      finalProjectData?.is_archived
                        ? undefined
                        : handleChangeRevisionSort
                    }
                    onUploadRevision={
                      showUploadRevision ? handleUploadRevision : undefined
                    }
                    onStartWorking={
                      startWorking === "hidden"
                        ? undefined
                        : () => setConfirmStartWorking(true)
                    }
                    startWorkingDisabled={startWorking === "disabled"}
                    startWorkingBusy={startingWork}
                    uploadDisabled={showStartWorking}
                  />
                  {revisionsLoading && revisions.length > 0 && (
                    <div className="flex items-center justify-center py-8">
                      <Spinner />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {activeTab === ProjectDetailTabs.CHAT_BOARD && (
            <ChatBoard
              projectId={id}
              chatBoardId={getSelectedChatBoardId()}
              isReadOnly={
                finalProjectData?.status?.toLowerCase() === "completed" ||
                !!finalProjectData?.is_archived
              }
              mobileFilterDropdown={
                chatBoardOptions.length > 0 ? (
                  <div
                    className={classNames(
                      "md:hidden flex justify-end mt-2",
                      styles.mobileFilterDropdown,
                    )}
                  >
                    <FilterDropdown
                      label=""
                      options={chatBoardOptions}
                      selected={selectedChatBoard}
                      onSelect={(value: string) => setSelectedChatBoard(value)}
                    />
                  </div>
                ) : undefined
              }
            />
          )}
          {activeTab === ProjectDetailTabs.FEEDBACK_REQUEST && (
            <FeedbackRequest
              revisionId={selectedRevisionId}
              selectedRevisionName={selectedRevision}
              projectId={id ? parseInt(id) : undefined}
              projectTitle={finalProjectData?.name || finalProjectData?.title}
              isRevisionListLoading={revisionListLoading}
              isInitialRevisionListLoad={revisionPage === 1}
              hasRevisions={revisionList.length > 0}
              isArchived={!!finalProjectData?.is_archived}
              mobileFilterDropdown={
                <div
                  className={classNames(
                    "md:hidden flex justify-end mt-2",
                    styles.mobileFilterDropdown,
                  )}
                >
                  <FilterDropdown
                    label=""
                    options={revisionList.map(
                      (rev) => rev.name || `Revision ${rev.revision_id || ""}`,
                    )}
                    selected={selectedRevision || "Select a revision"}
                    menuWidth="fit-content"
                    maxHeight="200px"
                    menuPosition="right"
                    loading={revisionListLoading}
                    onScrollEnd={
                      hasMoreRevisions && !revisionListLoading
                        ? loadMoreRevisions
                        : undefined
                    }
                    onSelect={(value) => {
                      setSelectedRevision(value);
                      const selected = revisionList.find(
                        (rev) =>
                          (rev.name || `Revision ${rev.revision_id || ""}`) ===
                          value,
                      );
                      setSelectedRevisionId(selected?.revision_id);
                    }}
                  />
                </div>
              }
            />
          )}
          {activeTab === ProjectDetailTabs.PROJECT_OVERVIEW && (
            <ProjectOverview
              projectId={id ? parseInt(id) : undefined}
              contractDocuments={finalProjectData?.contract_documents}
              questionnaireDocuments={finalProjectData?.questionnaire_documents}
              inspirationFiles={finalProjectData?.inspiration_files}
            />
          )}
          {activeTab === ProjectDetailTabs.COLLABORATORS && (
            <ProjectCollaborators
              projectId={id ? parseInt(id) : undefined}
            />
          )}
        </div>
      </div>
      {editingRevision && (
        <EditRevisionModal
          revisionId={editingRevision.id}
          initialTitle={editingRevision.title}
          initialDesignerComment={editingRevision.designerComment}
          initialInternalComment={editingRevision.internalComment}
          initialFiles={editingRevision.files}
          isFinalPlans={editingRevision.isFinalPlans}
          onClose={() => setEditingRevision(null)}
          onSaved={(title) => handleRevisionSaved(editingRevision.id, title)}
        />
      )}
      <StartWorkingConfirmationModal
        show={confirmStartWorking}
        handleClose={() => setConfirmStartWorking(false)}
        handleSubmit={handleStartWorking}
        loading={startingWork}
      />
    </div>
  );
};

export default DesignerProjectDetail;
